import { useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, CircleDot, Circle, CreditCard, FileText, Loader2, Plus, ShoppingBag, Store, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, euro, fmtDays, fmtRange, imageUri, SIZE_HEIGHTS } from "@/lib/noleggio";
import { useAuth } from "@/lib/auth";
import { useCart } from "@/lib/cart";
import { useI18n } from "@/lib/i18n";
import { CustomerForm, customerErrors, docExpiredFor, EMPTY_CUSTOMER, fromIsoDate, toIsoDate } from "@/components/CustomerForm";
import ContractDialog from "@/components/ContractDialog";

const METHODS = [
  { key: "store", title: "pay_store", sub: "pay_store_sub", icon: Store },
  { key: "stripe", title: "pay_card", sub: "coming_soon", icon: CreditCard, disabled: true },
  { key: "paypal", title: null, sub: "coming_soon", icon: Wallet, disabled: true },
];
const sameSlot = (a, b) => a.bike_id === b.bike_id && a.size === b.size && a.date === b.date && Number(a.days) === Number(b.days);

export default function Checkout() {
  const [sp] = useSearchParams();
  const { user, loading } = useAuth();
  const cart = useCart();
  const { t } = useI18n();
  const direct = sp.get("bike_id");
  if (loading) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin" color="var(--brand-primary)" /></div>;
  if (!user) return <Navigate to={`/accedi?next=${encodeURIComponent(`/checkout?${sp.toString()}`)}`} replace />;
  const items = direct ? [{ key: "direct", bike_id: direct, size: sp.get("size"), date: sp.get("date"), days: Number(sp.get("days")) }] : cart.items;
  if (!items.length) {
    return (
      <div data-testid="cart-empty" className="py-24 flex flex-col items-center gap-3 text-center px-6">
        <ShoppingBag size={48} color="var(--muted)" />
        <p className="text-lg font-bold">{t("cart_empty")}</p>
        <Link to="/" data-testid="cart-browse" className="mn-btn mn-btn-ghost">{t("browse_bikes")}</Link>
      </div>
    );
  }
  return <CheckoutForm items={items} fromCart={!direct} user={user} />;
}

function CheckoutForm({ items, fromCart, user }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const cart = useCart();
  const { t } = useI18n();
  const bodyOf = (i) => ({ bike_id: i.bike_id, size: i.size, mode: "daily", date: i.date, days: Number(i.days) });
  const bikeQs = useQueries({ queries: items.map((i) => ({ queryKey: ["bike", i.bike_id], queryFn: () => api(`/bikes/${i.bike_id}`) })) });
  const quoteQs = useQueries({ queries: items.map((i) => ({ queryKey: ["quote-checkout", bodyOf(i)], queryFn: () => api(`/bikes/${i.bike_id}/quote`, { method: "POST", body: { ...bodyOf(i), payment_method: "store" } }) })) });
  const contractQ = useQuery({ queryKey: ["contract"], queryFn: () => api("/contract"), staleTime: 300000 });
  const savedQ = useQuery({ queryKey: ["my-customer", user.id], queryFn: () => api("/me/customer") });

  const [draft, setDraft] = useState(null);
  const [phone, setPhone] = useState(user.phone ?? "");
  const [showErr, setShowErr] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [acceptedVersion, setAcceptedVersion] = useState(null);
  const [busy, setBusy] = useState(false);

  const saved = savedQ.data;
  const customer = draft ?? (saved && Object.keys(saved).length
    ? { ...EMPTY_CUSTOMER, ...saved, birth_date: fromIsoDate(saved.birth_date) }
    : { ...EMPTY_CUSTOMER, full_name: user.name ?? "" });
  const contractOk = !!acceptedVersion && acceptedVersion === contractQ.data?.version;
  const phoneOk = phone.replace(/\D/g, "").length >= 6;

  const failedQuote = quoteQs.find((q) => q.isError);
  if (failedQuote || bikeQs.some((q) => !q.data) || quoteQs.some((q) => !q.data)) {
    return (
      <div className="py-32 flex flex-col items-center gap-4 text-[var(--muted)]">
        {failedQuote ? <>{failedQuote.error.message}<button data-testid="checkout-retry" className="mn-btn mn-btn-ghost" onClick={() => failedQuote.refetch()}>{t("retry")}</button></> : <Loader2 className="animate-spin" color="var(--brand-primary)" />}
      </div>
    );
  }

  const rows = items.map((it, i) => {
    const quote = quoteQs[i].data;
    const wanted = items.filter((o) => sameSlot(o, it)).length;
    return { it, bike: bikeQs[i].data, quote, short: (quote.available?.[it.size] ?? 0) < wanted };
  });
  const total = rows.reduce((s, r) => s + r.quote.total, 0);
  const lastDay = rows.map((r) => r.quote.end.slice(0, 10)).sort().pop();
  const expired = !!docExpiredFor(customer, lastDay);
  const anyShort = rows.some((r) => r.short);

  const confirm = async () => {
    if (customerErrors(customer).length) {
      setShowErr(true);
      toast.error(t("cust_invalid"));
      return;
    }
    setBusy(true);
    const done = [];
    try {
      for (const r of rows) {
        await api("/bookings", { method: "POST", body: {
          ...bodyOf(r.it), payment_method: "store", phone: phone.trim(),
          contract_accepted: true, clauses_approved: true, privacy_accepted: true, contract_version: acceptedVersion,
          customer: { ...customer, birth_date: toIsoDate(customer.birth_date), tax_code: customer.tax_code.trim().toUpperCase() },
        } });
        done.push(r.it.key);
      }
      if (fromCart) cart.removeMany(done);
      qc.invalidateQueries();
      nav(`/confermata?n=${done.length}`, { replace: true });
    } catch (e) {
      if (fromCart && done.length) cart.removeMany(done);
      if (done.length) qc.invalidateQueries();
      if (e.status === 409 && /contrac?tt?o?/i.test(e.message)) {
        setAcceptedVersion(null);
        contractQ.refetch();
      }
      toast.error(done.length ? t("cart_partial", { n: done.length, msg: e.message }) : e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 pt-6 pb-36 md:pb-0">
      <div className="flex items-center gap-2 mb-6">
        <button data-testid="checkout-back" onClick={() => nav(-1)} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white"><ChevronLeft /></button>
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">{t("confirm_booking")}</h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_380px] gap-6 items-start [&>*]:min-w-0">
        <div className="flex flex-col gap-3 order-2 md:order-1">
          <p className="mn-section !mt-0">{t("payment_method")}</p>
          {METHODS.map((m) => {
            const on = m.key === "store";
            return (
              <div key={m.key} data-testid={`payment-method-${m.key}`} className={`mn-card p-4 flex items-center gap-3 border-[1.5px] ${on ? "border-[var(--brand-primary)]" : "border-white opacity-50"}`}>
                <m.icon color={on ? "var(--brand-primary)" : "#2c2c2e"} />
                <div className="flex-1"><p className="font-bold">{m.title ? t(m.title) : "PayPal"}</p><p className="text-sm text-[var(--text-2)]">{t(m.sub)}</p></div>
                {on ? <CircleDot color="var(--brand-primary)" /> : <Circle color="#c7c7cc" />}
              </div>
            );
          })}
          <p className="mn-section">{t("cust_title")}</p>
          {rows.length > 1 && <p data-testid="cart-one-holder" className="text-sm text-[var(--text-2)] -mt-1">{t("cart_holder", { n: rows.length })}</p>}
          <CustomerForm value={customer} onChange={setDraft} showErrors={showErr} lastDay={lastDay} />
          <p className="mn-section">{t("phone")}</p>
          <input data-testid="checkout-phone-input" className={`mn-input !bg-white ${showErr && !phoneOk ? "mn-input-bad" : ""}`} type="tel" autoComplete="tel" placeholder="333 1234567" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <p className="mn-section">{t("contract_title")}</p>
          <button data-testid="contract-open" onClick={() => setContractOpen(true)} className={`mn-card p-4 flex items-center gap-3 text-left border-[1.5px] transition-colors ${contractOk ? "border-[var(--brand-primary)]" : "border-white hover:border-[var(--brand)]"}`}>
            {contractOk ? <CheckCircle2 color="var(--brand-primary)" /> : <FileText color="#2c2c2e" />}
            <div className="flex-1">
              <p className="font-bold">{contractOk ? t("contract_accepted") : t("contract_read")}</p>
              <p className="text-sm text-[var(--text-2)]">{contractQ.isError ? contractQ.error.message : contractOk ? t("contract_reread") : t("contract_required")}</p>
            </div>
            <ChevronRight color="var(--muted)" />
          </button>
        </div>

        <div className="md:sticky md:top-24 flex flex-col gap-3 order-1 md:order-2">
          {rows.map(({ it, bike, quote, short }) => (
            <div key={it.key} data-testid={`checkout-item-${it.key}`} className={`mn-card p-3 flex gap-3 items-center border-[1.5px] ${short ? "border-[var(--error)]" : "border-white"}`}>
              <img src={imageUri(bike.image_url)} alt="" className="w-16 h-16 rounded-xl object-cover bg-[var(--surface-3)]" />
              <div className="flex-1 min-w-0 text-sm">
                <p className="font-bold truncate">{bike.name}</p>
                <p className="text-[var(--text-2)]">{t("size_x", { s: it.size })} · {SIZE_HEIGHTS[it.size]}</p>
                <p className="text-[var(--text-2)]">{fmtRange(quote)}</p>
                <p className="text-[var(--text-2)]">{fmtDays(quote.units)}</p>
                {short && <p data-testid={`checkout-item-short-${it.key}`} className="text-xs font-bold text-[var(--error)]">{t("cart_short")}</p>}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="font-extrabold">{euro(quote.total)}</span>
                {fromCart && <button data-testid={`cart-remove-${it.key}`} onClick={() => cart.remove(it.key)} className="w-8 h-8 rounded-full flex items-center justify-center text-[var(--muted)] hover:text-[var(--error)] hover:bg-red-50"><Trash2 size={16} /></button>}
              </div>
            </div>
          ))}
          <Link to="/" data-testid="cart-add-more" className="mn-card p-3 flex items-center justify-center gap-2 text-sm font-bold text-[var(--brand-primary)] border-[1.5px] border-dashed border-[var(--brand-secondary)] hover:bg-[var(--brand-tertiary)] transition-colors">
            <Plus size={16} /> {t("cart_add_more")}
          </Link>
          <div className="mn-card p-4 flex justify-between items-center">
            <span className="font-bold text-base">{t("total")}{rows.length > 1 ? ` · ${t("cart_bikes", { n: rows.length })}` : ""}</span>
            <span data-testid="checkout-total" className="text-2xl font-extrabold">{euro(total)}</span>
          </div>
          <div className="fixed md:static inset-x-0 bottom-16 z-20 p-4 md:p-0 bg-white/90 md:bg-transparent backdrop-blur-xl md:backdrop-blur-none border-t md:border-0 border-[var(--border)] flex flex-col gap-2">
            <button data-testid="confirm-booking-button" className="mn-btn w-full" disabled={busy || !phoneOk || !contractQ.data || expired || anyShort}
              onClick={contractOk ? confirm : () => setContractOpen(true)}>
              {busy && <Loader2 size={18} className="animate-spin" />}
              {contractOk ? (rows.length > 1 ? t("cart_confirm_n", { n: rows.length }) : t("confirm_booking")) : t("contract_read")}
            </button>
            {!phoneOk && <p className="text-xs text-[var(--muted)] text-center">{t("phone_needed")}</p>}
            {phoneOk && expired && <p data-testid="confirm-doc-expired-hint" className="text-xs font-semibold text-[var(--error)] text-center">{t("doc_expired_short")}</p>}
            {anyShort && <p data-testid="confirm-short-hint" className="text-xs font-semibold text-[var(--error)] text-center">{t("cart_short")}</p>}
          </div>
        </div>
      </div>
      {contractOpen && (
        <ContractDialog contract={contractQ.data} onClose={() => setContractOpen(false)}
          onAccept={() => { setAcceptedVersion(contractQ.data?.version ?? null); setContractOpen(false); }} />
      )}
    </div>
  );
}
